import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Swipe rail + pinch/double-tap/wheel zoom + rotate, same constants and feel as ApplePhotoGallery.
 * The consumer renders prev/current/next slides translated by `dragX` (± viewport width).
 */
export const GALLERY = {
  MIN_SCALE: 1, MAX_SCALE: 4, ZOOM_IN_MULT: 2, DOUBLE_TAP_MS: 300, DOUBLE_TAP_DIST: 30,
  SWIPE_DIST: 80, SWIPE_VEL: 0.35, EDGE_RESIST: 0.35, ANGLE_LOCK: 28, SNAP_MS: 250,
};

interface Opts {
  index: number;
  count: number;
  onPrev: () => void;
  onNext: () => void;
  zoomEnabled: boolean;
  reducedMotion?: boolean;
}

export function useGalleryGestures({ index, count, onPrev, onNext, zoomEnabled, reducedMotion }: Opts) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  // Callback ref + state so listeners attach even when the element mounts late (dialog portals)
  const [, setMounted] = useState<HTMLDivElement | null>(null);
  const setViewport = useCallback((n: HTMLDivElement | null) => { viewportRef.current = n; setMounted(n); }, []);
  const [dragX, setDragX] = useState(0);
  const [animating, setAnimating] = useState(false);
  const [scale, setScale] = useState(1);
  const [panX, setPanX] = useState(0);
  const [panY, setPanY] = useState(0);
  const [rotation, setRotation] = useState(0);
  const isZoomed = scale > 1;

  const pointers = useRef(new Map<number, { x: number; y: number }>());
  const moves = useRef<{ x: number; t: number }[]>([]);
  const axis = useRef<null | 'h' | 'v'>(null);
  const start = useRef({ x: 0, y: 0 });
  const pinch = useRef({ dist: 0, scale: 1, cx: 0, cy: 0 });
  const lastTap = useRef({ t: 0, x: 0, y: 0 });
  const moved = useRef(false);
  const busy = useRef(false);
  const dragRef = useRef(0);

  const width = () => viewportRef.current?.clientWidth || window.innerWidth || 375;
  const clampPan = (x: number, y: number, sc: number) => {
    const el = viewportRef.current;
    const w = el?.clientWidth || 375, h = el?.clientHeight || 667;
    const mx = Math.max(0, (w * sc - w) / 2), my = Math.max(0, (h * sc - h) / 2);
    return { x: Math.min(mx, Math.max(-mx, x)), y: Math.min(my, Math.max(-my, y)) };
  };

  const resetZoom = useCallback(() => { setScale(1); setPanX(0); setPanY(0); }, []);
  const rotate = useCallback(() => setRotation(r => (r + 90) % 360), []);

  // Reset zoom/rotation whenever the slide changes
  useEffect(() => { resetZoom(); setRotation(0); setDragX(0); }, [index, resetZoom]);

  const zoomAt = useCallback((mult: number, clientX?: number, clientY?: number) => {
    if (!zoomEnabled) return;
    const vp = viewportRef.current;
    const next = Math.max(GALLERY.MIN_SCALE, Math.min(GALLERY.MAX_SCALE, scale * mult));
    if (!vp) { setScale(next); return; }
    const r = vp.getBoundingClientRect();
    const cx = (clientX ?? r.left + r.width / 2) - r.left - r.width / 2;
    const cy = (clientY ?? r.top + r.height / 2) - r.top - r.height / 2;
    const k = next / scale;
    const c = clampPan(panX - cx * (k - 1), panY - cy * (k - 1), next);
    setPanX(c.x); setPanY(c.y); setScale(next);
    if (next === 1) { setPanX(0); setPanY(0); }
  }, [scale, panX, panY, zoomEnabled]);

  /** Animate the rail out, then change slide. Used by swipes, arrows and keys. */
  const slide = useCallback((dir: 1 | -1) => {
    if (busy.current) return;
    if (dir === 1 && index >= count - 1) return;
    if (dir === -1 && index <= 0) return;
    if (reducedMotion) { (dir === 1 ? onNext : onPrev)(); return; }
    busy.current = true;
    setAnimating(true);
    setDragX(dir === 1 ? -width() : width());
    window.setTimeout(() => {
      setAnimating(false);
      setDragX(0);
      (dir === 1 ? onNext : onPrev)();
      busy.current = false;
    }, GALLERY.SNAP_MS);
  }, [index, count, onNext, onPrev, reducedMotion]);

  // Pointer events
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;

    const down = (e: PointerEvent) => {
      if ((e.target as HTMLElement).closest('button, a')) return;
      el.setPointerCapture?.(e.pointerId);
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.current.size === 1) {
        axis.current = null;
        moves.current = [{ x: e.clientX, t: performance.now() }];
        start.current = { x: e.clientX, y: e.clientY };
        dragRef.current = 0;
        moved.current = false;
        setAnimating(false);
      } else if (pointers.current.size === 2 && zoomEnabled) {
        const [a, b] = Array.from(pointers.current.values());
        pinch.current = { dist: Math.hypot(a.x - b.x, a.y - b.y), scale, cx: (a.x + b.x) / 2, cy: (a.y + b.y) / 2 };
      }
    };

    const move = (e: PointerEvent) => {
      const prev = pointers.current.get(e.pointerId);
      if (!prev) return;
      pointers.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      const pts = Array.from(pointers.current.values());

      if (pts.length === 2 && zoomEnabled) {
        moved.current = true;
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        const ns = Math.max(GALLERY.MIN_SCALE, Math.min(GALLERY.MAX_SCALE, (pinch.current.scale * dist) / Math.max(1, pinch.current.dist)));
        const r = el.getBoundingClientRect();
        const cx = pinch.current.cx - r.left - r.width / 2, cy = pinch.current.cy - r.top - r.height / 2;
        const k = ns / scale;
        const c = clampPan(panX - cx * (k - 1), panY - cy * (k - 1), ns);
        setPanX(c.x); setPanY(c.y); setScale(ns);
        setDragX(0);
        return;
      }

      if (scale > 1) {
        const dx = e.clientX - prev.x, dy = e.clientY - prev.y;
        if (Math.abs(dx) > 3 || Math.abs(dy) > 3) moved.current = true;
        const c = clampPan(panX + dx, panY + dy, scale);
        setPanX(c.x); setPanY(c.y);
        return;
      }

      const dxA = Math.abs(e.clientX - start.current.x), dyA = Math.abs(e.clientY - start.current.y);
      if (dxA > 3 || dyA > 3) moved.current = true;
      if (!axis.current && (dxA > 3 || dyA > 3)) {
        axis.current = (Math.atan2(dyA, dxA) * 180) / Math.PI <= GALLERY.ANGLE_LOCK ? 'h' : 'v';
      }
      if (axis.current !== 'h') return;
      let dx = e.clientX - start.current.x;
      if ((index === 0 && dx > 0) || (index === count - 1 && dx < 0)) dx *= GALLERY.EDGE_RESIST;
      dragRef.current = dx;
      setDragX(dx);
      const t = performance.now();
      moves.current.push({ x: e.clientX, t });
      moves.current = moves.current.filter(p => t - p.t <= 120);
    };

    const up = (e: PointerEvent) => {
      if (!pointers.current.has(e.pointerId)) return;
      pointers.current.delete(e.pointerId);
      if (pointers.current.size >= 1) return;

      if (e.pointerType === 'touch' && zoomEnabled) {
        const now = performance.now();
        const near = Math.hypot(e.clientX - lastTap.current.x, e.clientY - lastTap.current.y) < GALLERY.DOUBLE_TAP_DIST;
        if (!moved.current && now - lastTap.current.t < GALLERY.DOUBLE_TAP_MS && near) {
          if (scale === 1) zoomAt(GALLERY.ZOOM_IN_MULT, e.clientX, e.clientY); else resetZoom();
          lastTap.current.t = 0;
          return;
        }
        if (!moved.current) lastTap.current = { t: now, x: e.clientX, y: e.clientY };
      }

      if (scale > 1 || axis.current !== 'h') { axis.current = null; return; }

      let v = 0;
      if (moves.current.length >= 2) {
        const f = moves.current[0], l = moves.current[moves.current.length - 1];
        v = (l.x - f.x) / Math.max(1, l.t - f.t);
      }
      const goPrev = (dragRef.current > GALLERY.SWIPE_DIST || v > GALLERY.SWIPE_VEL) && index > 0;
      const goNext = (dragRef.current < -GALLERY.SWIPE_DIST || v < -GALLERY.SWIPE_VEL) && index < count - 1;
      if (goPrev) slide(-1);
      else if (goNext) slide(1);
      else {
        setAnimating(!reducedMotion);
        setDragX(0);
        window.setTimeout(() => setAnimating(false), GALLERY.SNAP_MS);
      }
      axis.current = null;
      moves.current = [];
    };

    el.addEventListener('pointerdown', down);
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    return () => {
      el.removeEventListener('pointerdown', down);
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
    };
  });

  // Wheel: ctrl/⌘ zoom, pan when zoomed (native non-passive listener)
  useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const dy = e.deltaY * (e.deltaMode === 1 ? 16 : e.deltaMode === 2 ? 100 : 1);
        zoomAt(Math.exp(-dy * 0.0015), e.clientX, e.clientY);
      } else if (isZoomed) {
        e.preventDefault();
        const c = clampPan(panX - e.deltaX, panY - e.deltaY, scale);
        setPanX(c.x); setPanY(c.y);
      }
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  });

  /** Desktop single click toggles zoom (touch uses double-tap). */
  const onImageClick = (e: React.MouseEvent) => {
    const touch = navigator.maxTouchPoints > 0 || 'ontouchstart' in window;
    if (touch || moved.current) return;
    if (scale === 1) zoomAt(GALLERY.ZOOM_IN_MULT, e.clientX, e.clientY); else resetZoom();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    const tag = (e.target as HTMLElement).tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA') return;
    const k = e.key;
    if (k === 'ArrowRight') { e.preventDefault(); slide(1); }
    else if (k === 'ArrowLeft') { e.preventDefault(); slide(-1); }
    else if (k === '+' || k === '=') { e.preventDefault(); zoomAt(GALLERY.ZOOM_IN_MULT); }
    else if (k === '-') { e.preventDefault(); zoomAt(1 / GALLERY.ZOOM_IN_MULT); }
    else if (k === '0') { e.preventDefault(); resetZoom(); }
    else if ((k === 'r' || k === 'R') && zoomEnabled) { e.preventDefault(); rotate(); }
  };

  const railTransition = animating ? `transform ${GALLERY.SNAP_MS}ms cubic-bezier(.2,.8,.2,1)` : 'none';

  return {
    viewportRef: setViewport, dragX, railTransition, width, slide,
    scale, panX, panY, rotation, isZoomed, zoomAt, resetZoom, rotate, onImageClick, onKeyDown,
  };
}
